import { FC, useCallback, useState } from 'react';

import classnames from 'classnames';

import generateImageModalButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_image_modal_button_click';
import { Button, Loader, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import {
    ExclamationTriangleOutlinedSize32,
    ImageOutlinedSize32,
    PinOutlinedSize24,
    PlusOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';
import { CheckmarkOutlinedSize24 } from '@hh.ru/magritte-ui/product';

import useTrls from 'hooks/translation/useTrls';

import { cancelAiImageGeneration } from 'components/common/BannerImg/AiImageGenerationModal/GeneratedImages/cancelAiImageGeneration';
import { saveImageToGallery } from 'components/common/BannerImg/AiImageGenerationModal/GeneratedImages/saveImageToGallery';
import { selectGeneratedImage } from 'components/common/BannerImg/AiImageGenerationModal/GeneratedImages/selectGeneratedImage';
import { generateImage } from 'components/common/BannerImg/AiImageGenerationModal/generateImage';
import { StatusBlock } from 'components/common/BannerImg/AiImageGenerationModal/StatusBlock/StatusBlock';

import { AiImageQueuePosition, setGenerationQueueId, setImageLoaded } from 'store/models/aiImageGeneration';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/AiImageGenerationModal/GeneratedImages/GeneratedImages.module.less';

const Trls = {
    info: {
        part1: 'AdCreate.AdForm.BannerImg.generation.modal.info.part1',
        part2: 'AdCreate.AdForm.BannerImg.generation.modal.info.part2',
    },
    generation: {
        part1: 'AdCreate.AdForm.BannerImg.generation.modal.generation.part1',
        part2: 'AdCreate.AdForm.BannerImg.generation.modal.generation.part2',
    },
    longGenerations: {
        part1: 'AdCreate.AdForm.BannerImg.generation.modal.longGeneration.part1',
        part2: 'AdCreate.AdForm.BannerImg.generation.modal.longGeneration.part2',
    },
    cancel: 'AdCreate.AdForm.BannerImg.generation.modal.cancel',
    chooseImage: 'AdCreate.AdForm.BannerImg.generation.modal.chooseImage',
    error: {
        part1: 'AdCreate.AdForm.BannerImg.generation.error.part1',
        part2: 'AdCreate.AdForm.BannerImg.generation.error.part2',
    },
    repeat: 'AdCreate.AdForm.BannerImg.generation.modal.repeat',
    saveToGallery: 'AdCreate.AdForm.BannerImg.generation.modal.saveToGallery',
    saved: 'AdCreate.AdForm.BannerImg.generation.modal.saved',
};

export const GeneratedImages: FC<{ isQuickCampaign?: boolean }> = ({ isQuickCampaign }) => {
    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile, isXS } = useBreakpoint();

    const {
        generatedImages,
        generationPosition,
        isGenerationError,
        selectors,
        aiImageQueueId,
        savedImagesIds,
        imagesLoaded,
    } = useSelector((state) => state.aiImageGeneration);
    const [imagesLoadings, setSavedImagesLoadings] = useState<{ [id: number]: boolean }>(
        generatedImages.reduce((acc, image) => ({ ...acc, [image.id]: false }), {})
    );
    const adProfessions = useSelector((state) => state.createCampaign.adProfessions);

    const handleImageLoad = useCallback(
        (imageId: number) => {
            dispatch([setImageLoaded(imageId), setGenerationQueueId(null)]);
        },
        [dispatch]
    );

    const getGenerationInfoByPosition = useCallback(
        (generationPosition: AiImageQueuePosition | null) => {
            const ONE_MINUTE_MS = 60000;
            return (
                <div>
                    {generationPosition && generationPosition.waitingTimeMs > ONE_MINUTE_MS ? (
                        <>
                            <Text Element={isMobile ? 'span' : 'div'}>{trls(Trls.longGenerations.part1)}</Text>
                            <Text Element="span">
                                {trls(Trls.longGenerations.part2, [generationPosition.waitingTimeMs / ONE_MINUTE_MS])}
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text>{trls(Trls.generation.part1)}</Text>
                            <Text>{trls(Trls.generation.part2)}</Text>
                        </>
                    )}
                </div>
            );
        },
        [trls, isMobile]
    );

    return (
        <div className={styles.generatedImagesContainer}>
            {isGenerationError && (
                <StatusBlock
                    generatedImageLength={generatedImages.length}
                    content={
                        <div className={styles.statusBlock}>
                            <div>
                                <ExclamationTriangleOutlinedSize32 initialColor="negative" />
                            </div>
                            <VSpacing default={32} />
                            <Text>{trls(Trls.error.part1)}</Text>
                            <Text>{trls(Trls.error.part2)}</Text>
                            <VSpacing default={32} />
                            <div className={styles.button}>
                                <Button
                                    mode="secondary"
                                    stretched
                                    style="neutral"
                                    onClick={() => {
                                        selectors &&
                                            generateImageModalButtonClick({
                                                buttonName: 'retry',
                                                ...selectors,
                                            });
                                        void dispatch(generateImage(adProfessions[0], selectors));
                                    }}
                                >
                                    {trls(Trls.repeat)}
                                </Button>
                            </div>
                        </div>
                    }
                />
            )}
            {(!generatedImages.length || aiImageQueueId || generatedImages.some((image) => !imagesLoaded[image.id])) &&
                !isGenerationError && (
                    <StatusBlock
                        generatedImageLength={generatedImages.length}
                        content={
                            <div className={styles.statusBlock}>
                                {aiImageQueueId ? (
                                    <>
                                        <Loader size={32} color="secondary" />
                                        <VSpacing default={32} />
                                        <>{getGenerationInfoByPosition(generationPosition)}</>
                                        <VSpacing default={32} />
                                        <div className={styles.button}>
                                            <Button
                                                mode="secondary"
                                                style="neutral"
                                                stretched
                                                onClick={() => {
                                                    selectors &&
                                                        generateImageModalButtonClick({
                                                            buttonName: 'cancel',
                                                            ...selectors,
                                                        });
                                                    aiImageQueueId && dispatch(cancelAiImageGeneration(aiImageQueueId));
                                                }}
                                            >
                                                {trls(Trls.cancel)}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className={styles.icon}>
                                            <ImageOutlinedSize32 initialColor="secondary" />
                                        </div>
                                        <VSpacing default={32} />
                                        <Text>{trls(Trls.info.part1)}</Text>
                                        <Text>{trls(Trls.info.part2)}</Text>
                                    </>
                                )}
                            </div>
                        }
                    />
                )}
            {!!generatedImages.length &&
                generatedImages.map((image, index) => {
                    const isImageSaved = savedImagesIds.includes(image.id);

                    return (
                        <div key={image.id}>
                            {(index > 0 || aiImageQueueId) && <VSpacing default={20} />}
                            <div className={styles.imageWrapper}>
                                <div>
                                    <img
                                        src={image.url}
                                        alt="ai-generated-image"
                                        className={classnames(styles.image)}
                                        onLoad={() => handleImageLoad(image.id)}
                                    />
                                    {imagesLoaded[image.id] && (
                                        <>
                                            {isMobile && <VSpacing default={12} />}
                                            <div className={styles.imageButtons}>
                                                <Button
                                                    mode="secondary"
                                                    style="neutral"
                                                    stretched={isMobile}
                                                    icon={
                                                        isImageSaved ? (
                                                            <CheckmarkOutlinedSize24 />
                                                        ) : (
                                                            <PinOutlinedSize24 />
                                                        )
                                                    }
                                                    onClick={() => {
                                                        selectors &&
                                                            generateImageModalButtonClick({
                                                                buttonName: 'save_generated_image',
                                                                ...selectors,
                                                            });
                                                        void dispatch(
                                                            saveImageToGallery({
                                                                id: image.id,
                                                                setSavedImagesLoadings,
                                                            })
                                                        );
                                                    }}
                                                    disabled={isImageSaved}
                                                    loading={imagesLoadings[image.id]}
                                                    size={isMobile ? 'small' : 'medium'}
                                                >
                                                    {trls(isImageSaved ? Trls.saved : Trls.saveToGallery)}
                                                </Button>
                                                <Button
                                                    mode="secondary"
                                                    style="neutral"
                                                    icon={<PlusOutlinedSize24 />}
                                                    onClick={() => {
                                                        selectors &&
                                                            generateImageModalButtonClick({
                                                                buttonName: 'choose_generated_image',
                                                                ...selectors,
                                                            });
                                                        void dispatch(selectGeneratedImage(image, !!isQuickCampaign));
                                                    }}
                                                    size={isMobile ? 'small' : 'medium'}
                                                    stretched={isXS}
                                                >
                                                    {trls(Trls.chooseImage)}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {index === generatedImages.length - 1 && <VSpacing default={16} />}
                        </div>
                    );
                })}
        </div>
    );
};
